import httpService from "@src/utility/httpService";
import { ENDPOINTS, QUERY } from "./apiUrlConstants";
import { config } from '@uhg-abyss/web/tools/config';
import { checkIsInternal } from "@src/utility/createStorage";
import { handleError } from "@src/utility/commonUtilityServices";

const _httpService = new httpService();

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
}

export const configLoggedUser = (): void => {

  console.warn('Config session values service called!');
};

export const configSessionValues = (): Promise<void> => {
  return Promise.resolve();
};

export const getTopSearchedList = (): Promise<any> => {
  const endpoint = sessionStorage.getItem('isPSMSReportCardClicked') === 'true'
    ? ENDPOINTS.PSMS_ENTITY_TIN_SEARCH
    : ENDPOINTS.PROVIDER_TIN_SEARCH;
  return _httpService.get(endpoint).catch((e: unknown) => {
    handleError('Error in getTopSearchedList', e);
    throw new Error('Failed to fetch top searched list. Please try again later.');
  });
};

export const fetchUserSavedTins = (payload: { UserId: string }): Promise<any> => {
  return _httpService.push(
    ENDPOINTS.FETCH_USER_SAVED_TINS,
    { [QUERY.USER_ID]: payload.UserId }
  ).catch((e: unknown) => {
    handleError('Error in fetchUserSavedTins', e);
    throw new Error('Failed to fetch user saved TINs. Please try again later.');
  });
};

export const getGeoSegmentationSearchList = (payload: { searchValue: string, page?: number, limit?: number } = { searchValue: '', page: 1, limit: 20 }): Promise<any> => {
  return _httpService.push(
    ENDPOINTS.GEO_SEGMENTATION_SEARCH,
    { [QUERY.SEARCH_VALUE_GEO]: payload?.searchValue, [QUERY.PAGE]: payload?.page, [QUERY.LIMIT]: payload?.limit }
  ).catch((e: unknown) => {
    handleError('Error in getGeoSegmentationSearchList', e);
    throw new Error('Failed to fetch geo-segmentation search list. Please try again later.');
  });
};

const sanitizeInput = (input: string): string => {
  return input.replace(/[^\w\s,]/gi, '');
};

export const getProviderSearchList = (payload: { searchValue: string, type?: string } = { searchValue: '', type: 'hco' }): Promise<any> => {
  const sanitizedSearchValue = sanitizeInput(payload.searchValue);
  return _httpService.push(
    `${ENDPOINTS.PROVIDER_SEARCH}?${QUERY.SEARCH_TYPE}=${payload?.type}`,
    { [QUERY.SEARCH_VALUE_PROVIDER]: sanitizedSearchValue }
  ).catch((e: unknown) => {
    handleError('Error in getProviderSearchList', e);
    throw new Error('Failed to fetch provider search list. Please try again later.');
  });
};

export const getTaxIdSearchList = (payload: { searchValue: string, type?: string, page?: number, limit?: number } = { searchValue: '', type: 'tinname', page: 1, limit: 20 }): Promise<any> => {
  return _httpService.push(
    `${ENDPOINTS.EXTENDED_TINS_SEARCH}?${QUERY.SEARCH_TYPE}=${payload?.type}&${QUERY.LIMIT}=${payload?.limit}&${QUERY.PAGE}=${payload?.page}`,
    { [QUERY.SEARCH_VALUE_TIN]: payload?.searchValue }
  ).catch((e: unknown) => {
    handleError('Error in getTaxIdSearchList', e);
    throw new Error('Failed to fetch tax ID search list. Please try again later.');
  });
};

export const getContractIdSearchList = async (payload: { searchValue: string, page?: number, limit?: number } = { searchValue: '', page: 1, limit: 20 }): Promise<any> => {
  const searchValue = (payload?.searchValue || '').trim();
  return _httpService.push(
    `${ENDPOINTS.CONTRACT_SEARCH}?${QUERY.SEARCH_TYPE}=contract`,
    { [QUERY.SEARCH_VALUE_PROVIDER]: searchValue }
  ).catch((e: unknown) => {
    handleError('Error in getContractIdSearchList', e);
    throw new Error('Failed to fetch contract ID search list. Please try again later.');
  });
};

// payload.searchValue must be a comma-separated list of ContractIds
export const getMultiContractIdSearchList = (payload: { searchValue: string } = { searchValue: '' }): Promise<any> => {
  return _httpService.push(
    `${ENDPOINTS.CONTRACT_SEARCH}?${QUERY.SEARCH_TYPE}=multicontract`,
    { [QUERY.SEARCH_VALUE_TIN]: payload?.searchValue }
  ).catch((e: unknown) => {
    handleError('Error in getMultiContractIdSearchList', e);
    throw new Error('Failed to fetch contract ID TIN counts. Please try again later.');
  });
};

export const saveSearchDetails = (payload: { SearchType: string, SearchString: string | number }): Promise<any> => {
  return _httpService.push(ENDPOINTS.SAVE_SEARCH_DETAILS, payload).catch((e: unknown) => {
    handleError('Error in saveSearchDetails', e);
    throw new Error('Failed to save search details. Please try again later.');
  });
};

export const saveContractSearchDetails = (payload: { SearchedContractId: number }): Promise<any> => {
  return _httpService.push(ENDPOINTS.SAVE_CONTRACT_SEARCH_DETAILS, payload).catch((e: unknown) => {
    handleError('Error in saveContractSearchDetails', e);
    throw new Error('Failed to save contract search details. Please try again later.');
  });
};

export const psmsSaveSearchDetails = (payload: { SearchType: string, SearchString: string | number }): Promise<any> => {
  return _httpService.push(ENDPOINTS.PSMS_SAVE_SEARCH_DETAILS, payload).catch((e: unknown) => {
    handleError('Error in psmsSaveSearchDetails', e);
    throw new Error('Failed to save PSMS search details. Please try again later.');
  });
};

export const saveTinsGroup = (payload: { UserId: string, UserType: string, GroupName: string, Status: string, LastViewed: boolean, SelectionType: string, Tin: string }): Promise<any> => {
  return _httpService.push(ENDPOINTS.SAVE_TINS_GROUP, payload).catch((e: unknown) => {
    handleError('Error in saveTinsGroup', e);
    throw new Error('Failed to save TINs group. Please try again later.');
  });
};

export const saveGeoSearchDetails = (payload: { Regions: string[], Markets: string[], States: string[] }): Promise<any> => {
  return _httpService.push(ENDPOINTS.SAVE_GEO_SEARCH_DETAILS, payload).catch((e: unknown) => {
    handleError('Error in saveGeoSearchDetails', e);
    throw new Error('Failed to save geo search details. Please try again later.');
  });
};

export const psmsSaveGeoSearchDetails = (payload: { Regions: string[], Markets: string[], States: string[] }): Promise<any> => {
  return _httpService.push(ENDPOINTS.PSMS_SAVE_GEO_SEARCH_DETAILS, payload).catch((e: unknown) => {
    handleError('Error in psmsSaveGeoSearchDetails', e);
    throw new Error('Failed to save PSMS geo search details. Please try again later.');
  });
};

export const getPSMSTaxIdSearchList = (payload: { searchValue: string, type?: string, page?: number, limit?: number } = { searchValue: '', type: 'tinname', page: 1, limit: 20 }): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response: any = await _httpService.push(
        `${ENDPOINTS.PSMS_PROVIDER_TIN_SEARCH}?${QUERY.SEARCH_TYPE}=${payload?.type}&${QUERY.LIMIT}=${payload?.limit}&${QUERY.PAGE}=${payload?.page}`,
        { [QUERY.SEARCH_VALUE_TIN]: payload?.searchValue }
      );
      resolve(response);
    } catch (e) {
      handleError('Error in getPSMSTaxIdSearchList', e);
      reject(new Error('Failed to fetch PSMS tax ID search list. Please try again later.'));
    }
  });
};

export const getPSMSProviderSearchList = (payload: { searchValue: string, type?: string } = { searchValue: '', type: 'hco' }): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const sanitizedSearchValue = sanitizeInput(payload.searchValue);
      const response: any = await _httpService.push(
        `${ENDPOINTS.PSMS_PROVIDER_SEARCH}?${QUERY.SEARCH_TYPE}=${payload?.type}`,
        { [QUERY.SEARCH_VALUE_PROVIDER]: sanitizedSearchValue }
      );
      resolve(response);
    } catch (e) {
      handleError('Error in getPSMSProviderSearchList', e);
      reject(new Error('Failed to fetch PSMS provider search list. Please try again later.'));
    }
  });
};
